// src/pages/InsuranceRequests.jsx (liste « Mes demandes de devis »)
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function fmtDate(d, locale) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(d);
  }
}

function fmtDateOnly(d, locale) {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(locale, { dateStyle: "medium" });
  } catch {
    return String(d);
  }
}

function badgeClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "bg-amber-100 text-amber-800 border-amber-200";
  if (s === "seen") return "bg-blue-100 text-blue-800 border-blue-200";
  if (s === "answered" || s === "quoted") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "rejected") return "bg-red-100 text-red-800 border-red-200";
  if (s === "done") return "bg-gray-100 text-gray-700 border-gray-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
}

function insuranceContextKey(coverageContext) {
  if (coverageContext === "home_premium") return "home_premium";
  if (coverageContext === "fine_art_policy") return "fine_art_policy";
  return null;
}

function policyKindKey(policyKind) {
  if (policyKind === "nail_to_nail") return "nail_to_nail";
  if (policyKind === "all_risks") return "all_risks";
  return null;
}


export default function InsuranceRequests() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "de" ? "de-DE" : i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR";
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");

      const { data: uRes, error: uErr } = await supabase.auth.getUser();
      if (uErr) throw uErr;
      const user = uRes?.user;
      if (!user?.id) throw new Error(t("insuranceRequests.errors.noSession"));

      const { data, error } = await supabase
        .from("insurance_requests")
        .select(
          "id,created_at,status,request_type,insured_value,currency,payload,artwork_id,coverage_context,policy_kind,transport_needed,event_start_date,event_end_date,storage_location"
        )
        .eq("created_by", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      console.error(e);
      setErr(e?.message || t("insuranceRequests.errors.load"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto">
      {/* Hero banner avec accroche GenZ/Millennial */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="text-2xl md:text-3xl font-serif font-semibold text-gray-900">
              {t("insuranceRequests.hero.title")}
            </div>
            <div className="text-gray-700 mt-2 text-base">
              {t("insuranceRequests.hero.subtitle")}
            </div>
          </div>
          <div className="hidden md:block text-5xl">🛡️</div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/insurance/request")}
            className="px-5 py-3 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-all"
          >
            {t("insuranceRequests.actions.newRequest")}
          </button>
          <button
            onClick={load}
            className="px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all"
          >
            {t("insuranceRequests.actions.refresh")}
          </button>
        </div>
      </div>

      {/* Bloc pédagogique : Pourquoi assurer ? */}
      {rows.length === 0 && !loading && (
        <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="text-3xl">💡</div>
            <div className="flex-1">
              <div className="text-lg font-semibold text-gray-900 mb-2">
                {t("insuranceRequests.education.whyInsure.title")}
              </div>
              <div className="text-sm text-gray-700 space-y-2">
                <p>{t("insuranceRequests.education.whyInsure.protection")}</p>
                <p>{t("insuranceRequests.education.whyInsure.affordable")}</p>
                <p className="text-xs text-gray-600 italic">{t("insuranceRequests.education.whyInsure.note")}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bloc pédagogique : Gagner du temps grâce aux infos partagées */}
      <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-5">
        <div className="flex items-start gap-3">
          <div className="text-2xl">⚡</div>
          <div className="flex-1 text-sm">
            <div className="font-semibold text-gray-900 mb-2">
              {t("insuranceRequests.education.sharing.title")}
            </div>
            <div className="text-gray-700 space-y-1.5">
              <p>{t("insuranceRequests.education.sharing.body")}</p>
              <p>{t("insuranceRequests.education.sharing.result")}</p>
              <p className="text-xs text-gray-600 italic">{t("insuranceRequests.education.sharing.note")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bloc info : Accès tiers limité */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
          🔐 <span>{t("insuranceRequests.privacy.title")}</span>
        </div>
        <div className="text-sm text-gray-700 mt-2 space-y-1.5">
          <p>{t("insuranceRequests.privacy.body")}</p>
          <p>{t("insuranceRequests.privacy.scope")}</p>
        </div>
      </div>

      {/* Message d'erreur */}
      {err ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {err}
        </div>
      ) : null}

      {/* Liste des demandes */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-sm text-gray-500">{t("insuranceRequests.loading")}</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-white p-12 text-center space-y-3">
          <div className="text-4xl">📋</div>
          <div className="text-gray-500">{t("insuranceRequests.empty.title")}</div>
          <div className="text-sm text-gray-600 max-w-md mx-auto">
            {t("insuranceRequests.empty.body")}
          </div>
          <button
            onClick={() => navigate("/insurance/request")}
            className="mt-4 px-5 py-2.5 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-all inline-block"
          >
            {t("insuranceRequests.empty.cta")}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const p = r.payload || {};
            const a = p.artwork || {};
            const title = a.title || p.artwork_title || t("insuranceRequests.artworkFallback");
            const artist = a.artist || p.artwork_artist || "";
            const contextKey =
              insuranceContextKey(r.coverage_context) ||
              insuranceContextKey(p?.insurance?.coverage_context);
            const policyKey =
              policyKindKey(r.policy_kind) ||
              policyKindKey(p?.insurance?.policy_kind);
            const ctxLabel = contextKey ? t(`insuranceRequests.coverage.${contextKey}`) : null;
            const polLabel = policyKey ? t(`insuranceRequests.policy.${policyKey}`) : null;

            const transportNeeded =
              typeof r.transport_needed === "boolean"
                ? r.transport_needed
                : !!p?.insurance?.transport_needed;

            const startDate = r.event_start_date || p?.insurance?.event_start_date || null;
            const endDate = r.event_end_date || p?.insurance?.event_end_date || null;
            const storageLocation = r.storage_location || p?.insurance?.storage_location || null;

            return (
              <div 
                key={r.id} 
                className="rounded-2xl border border-gray-200 bg-white p-5 hover:border-gray-300 transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-gray-500 mb-2">{fmtDate(r.created_at, locale)}</div>
                    <div className="text-sm space-y-2">
                      <div className="font-medium text-gray-900 text-base">
                        {title} {artist ? <span className="text-gray-500">• {artist}</span> : null}
                      </div>

                      {(ctxLabel || polLabel || transportNeeded || startDate || endDate || storageLocation) ? (
                        <div className="text-xs text-gray-600 space-y-0.5 bg-gray-50 rounded-lg p-2.5">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-700">📄</span>
                            <span>
                              {[ctxLabel, polLabel].filter(Boolean).join(" • ")}
                              {transportNeeded ? ` • 🚚 ${t("insuranceRequests.labels.transport")}` : ""}
                            </span>
                          </div>
                          {(startDate || endDate) ? (
                            <div className="flex items-center gap-1.5">
                              <span>📅</span>
                              <span>{startDate ? fmtDateOnly(startDate, locale) : "—"} → {endDate ? fmtDateOnly(endDate, locale) : "—"}</span>
                            </div>
                          ) : null}
                          {storageLocation ? (
                            <div className="flex items-center gap-1.5">
                              <span>📍</span>
                              <span>{storageLocation}</span>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      <div className="text-gray-700">
                        <span className="text-gray-500">{t("insuranceRequests.labels.type")}:</span> {t(`insuranceRequests.requestType.${r.request_type}`, { defaultValue: r.request_type })}
                      </div>
                      <div className="text-gray-700">
                        <span className="text-gray-500">{t("insuranceRequests.labels.insuredValue")}:</span>{" "}
                        {r.insured_value !== null && r.insured_value !== undefined
                          ? `${Number(r.insured_value).toLocaleString(locale)} ${r.currency || "EUR"}`
                          : "—"}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2.5">
                    <span className={`text-xs px-2.5 py-1 rounded-lg border font-medium ${badgeClass(r.status)}`}>
                      {t(`insuranceRequests.status.${r.status}`, { defaultValue: r.status })}
                    </span>
                    <Link
                      to={`/insurance/requests/${r.id}`}
                      className="px-4 py-2 rounded-xl text-sm font-medium bg-gray-100 hover:bg-gray-200 transition-all"
                    >
                      {t("insuranceRequests.actions.viewDetails")}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer pédagogique : Rappel bénéfices */}
      {rows.length > 0 && (
        <div className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-5 text-sm text-gray-700">
          <div className="flex items-start gap-3">
            <div className="text-2xl">✨</div>
            <div className="space-y-1.5">
              <p className="font-medium text-gray-900">{t("insuranceRequests.footer.title")}</p>
              <p>{t("insuranceRequests.footer.body")}</p>
              <p className="text-xs text-gray-600 italic">{t("insuranceRequests.footer.note")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}